import { Injectable } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { PermissionService } from '../permissions/permission.service';
import { DatadogService } from '@app/shared/services/datadog/datadog.service';
import { SessionService } from '../session/session.service';

/**
 * Guard para validacao de permissoes de menu antes da navegacao
 * Implementa logging de tentativas de acesso negado
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionGuard implements CanActivate {
  private redirectInProgress = false;
  private lastRedirectTime = 0;
  private readonly MIN_REDIRECT_INTERVAL_MS = 1000;

  constructor(
    private permissionService: PermissionService,
    private router: Router,
    private datadogService: DatadogService,
    private sessionService: SessionService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | boolean {
    const startTime = performance.now();
    const targetRoute = state.url;

    // Rotas que nao precisam de validacao de permissao
    const publicRoutes = ['/no-permission', '/service-unavailable', '/error', '/browser-warning'];
    if (publicRoutes.some(r => targetRoute.startsWith(r))) {
      return true;
    }

    // Tenta carregar permissoes do cache primeiro
    const cachedPermissions = this.permissionService.loadPermissionsFromStorage();

    if (cachedPermissions) {
      return this.validatePermission(targetRoute, startTime);
    }

    // Se nao ha cache, busca do endpoint
    return this.permissionService.getPermissions(true).pipe(
      switchMap(() => of(this.validatePermissionSync(targetRoute, startTime))),
      catchError(error => {
        this.logAccessDenied(targetRoute, 'endpoint_unavailable', startTime);
        this.safeNavigate('/service-unavailable');
        return of(false);
      })
    );
  }

  /**
   * Valida permissao de forma sincrona
   */
  private validatePermissionSync(route: string, startTime: number): boolean {
    const normalizedRoute = route.split(';')[0];
    const hasPermission = this.permissionService.hasPermissionForRoute(normalizedRoute);
    const validationTime = performance.now() - startTime;

    // Log de performance se validacao demorar mais de 500ms
    if (validationTime > 500) {
      this.datadogService.logInfo('Validacao de permissao lenta', {
        route: normalizedRoute,
        validationTimeMs: validationTime
      });
    }

    if (!hasPermission) {
      this.logAccessDenied(normalizedRoute, 'permission_denied', startTime);
      this.safeNavigate('/no-permission', { attemptedRoute: normalizedRoute });
      return false;
    }

    return true;
  }

  /**
   * Valida permissao retornando Observable
   */
  private validatePermission(route: string, startTime: number): Observable<boolean> {
    return of(this.validatePermissionSync(route, startTime));
  }

  /**
   * Navegacao segura com prevencao de loops
   */
  private safeNavigate(path: string, queryParams?: any): void {
    if (!this.canRedirect()) {
      this.datadogService.sendErrorLogs('Possivel loop de redirecionamento detectado', {
        path: path
      });
      return;
    }

    this.startRedirect();
    const navigationExtras = queryParams ? { queryParams } : undefined;
    this.router.navigate([path], navigationExtras).then(() => {
      this.endRedirect();
    }).catch(() => {
      this.endRedirect();
    });
  }

  /**
   * Verifica se pode redirecionar (prevencao de loops)
   */
  private canRedirect(): boolean {
    const now = Date.now();
    if (this.redirectInProgress || (now - this.lastRedirectTime) < this.MIN_REDIRECT_INTERVAL_MS) {
      return false;
    }
    return true;
  }

  /**
   * Marca inicio do redirecionamento
   */
  private startRedirect(): void {
    this.redirectInProgress = true;
    this.lastRedirectTime = Date.now();
  }

  /**
   * Marca fim do redirecionamento
   */
  private endRedirect(): void {
    this.redirectInProgress = false;
  }

  /**
   * Registra log de acesso negado com campos obrigatorios para auditoria
   */
  private logAccessDenied(route: string, reason: string, startTime: number): void {
    const authUser = this.sessionService.getAuthUser();
    const validationTime = performance.now() - startTime;
    const cacheStatus = this.permissionService.getLastCacheStatus();
    const userId = this.permissionService.getCurrentUserId();

    // Log com campos obrigatorios conforme requisito de auditoria
    this.datadogService.logInfo('access_denied', {
      event: 'access_denied',
      timestamp: new Date().toISOString(),
      userId: authUser ? userId : 'unknown',
      businessId: this.extractBusinessId(authUser),
      gestor: authUser ? authUser.gestor : 'unknown',
      attemptedRoute: route,
      requiredPermission: this.extractRequiredPermission(route),
      sessionId: this.getSessionId(),
      userAgent: navigator.userAgent,
      cacheStatus: cacheStatus,
      reason: reason,
      validationTimeMs: validationTime
    });
  }

  /**
   * Extrai businessId do usuario autenticado
   */
  private extractBusinessId(authUser: any): string {
    if (!authUser) return 'unknown';
    return authUser.businessId || authUser.idConexao || authUser.usuario || 'unknown';
  }

  /**
   * Extrai permissao requerida da rota
   */
  private extractRequiredPermission(route: string): string {
    // Normaliza a rota para identificar a permissao requerida
    const normalizedRoute = route.replace(/^\/+|\/+$/g, '').split('?')[0];
    return normalizedRoute || 'unknown';
  }

  /**
   * Obtem ID da sessao do sessionStorage
   */
  private getSessionId(): string {
    try {
      const authUser = this.sessionService.getAuthUser();
      return authUser ? (authUser.idSessao || authUser.idConexao || 'unknown') : 'unknown';
    } catch {
      return 'unknown';
    }
  }
}
